import { useMemo, useState } from 'react'
import { ChevronDown } from 'lucide-react'

import { Button, Checkbox } from '@/components/ui'
import {
  Command,
  CommandEmpty,
  CommandGroup,
  CommandInput,
  CommandItem,
  CommandList,
} from '@/components/ui/command'
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from '@/components/ui/popover'
import { cn } from '@/lib/utils'

export interface MultiSelectOption {
  label: string
  value: string
  disabled?: boolean
}

export interface MultiSelectGroup {
  label: string
  options: MultiSelectOption[]
}

type MultiSelectOptions = MultiSelectOption[] | MultiSelectGroup[]

const isGroup = (
  option: MultiSelectOption | MultiSelectGroup,
): option is MultiSelectGroup => {
  return (option as MultiSelectGroup).options !== undefined
}

type MultiSelectProps = {
  options: MultiSelectOptions
  value?: MultiSelectOption[]
  onChange: (next: MultiSelectOption[]) => void
  placeholder?: string
  disabled?: boolean
  maxDisplay?: number
  triggerClassName?: string
  contentClassName?: string
  searchPlaceholder?: string
}

export function MultiSelect({
  options,
  value = [],
  onChange,
  placeholder = 'Select options',
  disabled = false,
  maxDisplay = 2,
  triggerClassName,
  contentClassName,
  searchPlaceholder = 'Search...',
}: MultiSelectProps) {
  const [open, setOpen] = useState(false)

  const { groups, flatOptions } = useMemo(() => {
    if (options.length > 0 && isGroup(options[0])) {
      const grouped = options as MultiSelectGroup[]
      return {
        groups: grouped,
        flatOptions: grouped.flatMap((group) => group.options),
      }
    }
    return {
      groups: null,
      flatOptions: options as MultiSelectOption[],
    }
  }, [options])

  const selectedLabels = value.map((option) => option.label)
  const displayedLabels =
    selectedLabels.length > maxDisplay
      ? `${selectedLabels.slice(0, maxDisplay).join(', ')} +${
          selectedLabels.length - maxDisplay
        }`
      : selectedLabels.join(', ')

  const handleToggle = (option: MultiSelectOption) => {
    const isSelected = value.some((item) => item.value === option.value)
    if (isSelected) {
      onChange(value.filter((item) => item.value !== option.value))
    } else {
      onChange([...value, option])
    }
  }

  const renderOption = (option: MultiSelectOption) => {
    const isSelected = value.some((item) => item.value === option.value)
    return (
      <CommandItem
        key={option.value}
        onSelect={() => handleToggle(option)}
        disabled={option.disabled}
        className="flex items-center gap-2"
      >
        <Checkbox checked={isSelected} disabled={option.disabled} />
        <span className="text-sm">{option.label}</span>
      </CommandItem>
    )
  }

  return (
    <Popover open={open} onOpenChange={setOpen} modal>
      <PopoverTrigger asChild>
        <Button
          type="button"
          variant="tertiary"
          className={cn(
            'w-full justify-between rounded-full border border-monochrome-120 bg-monochrome-3-60 text-monochrome-1',
            !value.length && 'text-monochrome-2',
            triggerClassName,
          )}
          disabled={disabled}
        >
          <span className="truncate text-left">
            {value.length ? displayedLabels : placeholder}
          </span>
          <ChevronDown className="h-4 w-4 text-monochrome-2" />
        </Button>
      </PopoverTrigger>
      <PopoverContent
        align="start"
        className={cn(
          'w-[var(--radix-popover-trigger-width)] bg-monochrome-3-60-solid border-monochrome-120 p-0 max-h-[320px] overflow-hidden',
          contentClassName,
        )}
      >
        <Command className="max-h-[320px] p-2">
          <CommandInput placeholder={searchPlaceholder} />
          <CommandList className="max-h-[240px] overflow-y-auto -mx-2">
            <CommandEmpty>No results found.</CommandEmpty>
            {groups ? (
              groups.map((group) => (
                <CommandGroup key={group.label} heading={group.label}>
                  {group.options.map(renderOption)}
                </CommandGroup>
              ))
            ) : (
              <CommandGroup>{flatOptions.map(renderOption)}</CommandGroup>
            )}
          </CommandList>
        </Command>
      </PopoverContent>
    </Popover>
  )
}
